import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import './CustomMultiSelect.css';
import DropDown from '@clayui/drop-down';
import { ClayCheckbox, ClayInput } from '@clayui/form';
import { UseFormRegister, FieldValues, FieldErrors } from 'react-hook-form';
import Button from '@clayui/button';
import { primaryButtonStyle } from '../../utils/commonUtil';

interface Props {
    value: string[];
    handleOnChange: (e: string[]) => void;
    options: {
        key: string;
        name: string;
    }[];
    name: string;
    register?: UseFormRegister<FieldValues>; // Made optional since it's not used
    placeholder?: string;
    required?: boolean;
    disabled?: boolean;
    errors?: FieldErrors<FieldValues>;
}

const CustomMultiSelectInline: React.FC<Props> = ({
    value = [],
    handleOnChange,
    options = [],
    name,
    placeholder,
    disabled,
    errors
}) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [filteredOptions, setFilteredOptions] = useState(options || []);
    const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
    const menuRef = useRef<HTMLDivElement>(null);

    const getBaseKey = (val: string) => {
        if (val.includes(': ')) {
            return val.split(': ')[0];
        }
        return val;
    };

    const isOtherOption = (opt: { key: string; name: string }) => {
        return (
            opt.name.toLowerCase().includes('other') ||
            opt.key.toLowerCase().includes('other')
        );
    };

    const isChecked = (itemKey: string) => {
        return (
            Array.isArray(value) && value.some((v) => getBaseKey(v) === itemKey)
        );
    };

    const getOtherInputValue = (itemKey: string) => {
        const found = value.find((v) => v.startsWith(`${itemKey}: `));
        if (found) {
            return found.substring(itemKey.length + 2);
        }
        return '';
    };

    useEffect(() => {
        if (inputValue) {
            setFilteredOptions(
                (options || []).filter((option) =>
                    option?.name
                        ?.toLowerCase()
                        .includes(inputValue.toLowerCase())
                )
            );
        } else {
            setFilteredOptions(options || []);
        }
    }, [inputValue, options]);

    const [otherErrors, setOtherErrors] = useState<{ [key: string]: string }>(
        {}
    );

    const validateOtherInputs = () => {
        const errors: { [key: string]: string } = {};
        let isValid = true;

        value.forEach((val) => {
            const baseKey = getBaseKey(val);
            const option = (options || []).find((opt) => opt.key === baseKey);
            if (option && isOtherOption(option)) {
                const otherVal = getOtherInputValue(baseKey);
                if (!otherVal.trim()) {
                    errors[baseKey] = 'Others value is required';
                    isValid = false;
                }
            }
        });

        setOtherErrors(errors);
        return isValid;
    };

    const handleSelect = (itemKey: string) => {
        const currentValues = Array.isArray(value) ? value : [];
        const isCurrentlyChecked = isChecked(itemKey);

        if (isCurrentlyChecked) {
            handleOnChange(
                currentValues.filter((v) => getBaseKey(v) !== itemKey)
            );
            // Clear error if unselected
            if (otherErrors[itemKey]) {
                const newErrors = { ...otherErrors };
                delete newErrors[itemKey];
                setOtherErrors(newErrors);
            }
        } else {
            handleOnChange([...currentValues, itemKey]);
        }
    };

    const handleOtherInputChange = (itemKey: string, newVal: string) => {
        // Enforce 10 word maximum
        const words = newVal
            .trim()
            .split(/\s+/)
            .filter((w) => w.length > 0);
        if (words.length > 10) {
            return;
        }

        const currentValues = Array.isArray(value) ? [...value] : [];
        const index = currentValues.findIndex((v) => getBaseKey(v) === itemKey);

        const updatedValue = newVal ? `${itemKey}: ${newVal}` : itemKey;

        if (index !== -1) {
            currentValues[index] = updatedValue;
            handleOnChange(currentValues);
        }

        // Clear error as user types
        if (newVal.trim() && otherErrors[itemKey]) {
            const newErrors = { ...otherErrors };
            delete newErrors[itemKey];
            setOtherErrors(newErrors);
        }
    };

    const updateMenuPosition = () => {
        if (triggerRef.current && isOpen) {
            const rect = triggerRef.current.getBoundingClientRect();
            setMenuStyle({
                position: 'fixed',
                top: `${rect.bottom}px`,
                left: `${rect.left}px`,
                width: `${rect.width}px`,
                minWidth: '200px',
                zIndex: 10000,
                backgroundColor: 'white',
                border: '1px solid #ced4da',
                maxHeight: '300px', // Increased to accommodate input
                overflowY: 'auto',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            });
        }
    };

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'auto';
        } else {
            document.body.style.overflow = 'auto';
        }
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) {
            updateMenuPosition();
            window.addEventListener('resize', updateMenuPosition);
            window.addEventListener('scroll', updateMenuPosition, true);
        } else {
            window.removeEventListener('resize', updateMenuPosition);
            window.removeEventListener('scroll', updateMenuPosition, true);
        }
        return () => {
            window.removeEventListener('resize', updateMenuPosition);
            window.removeEventListener('scroll', updateMenuPosition, true);
        };
    }, [isOpen]);

    useEffect(() => {
        // Validation check for portal backdrop
        const handleBackdropClick = (event: any) => {
            if (event.target.classList.contains('dropdown-portal-overlay')) {
                if (validateOtherInputs()) {
                    setIsOpen(false);
                }
            }
        };
        document.addEventListener('mousedown', handleBackdropClick);
        return () => {
            document.removeEventListener('mousedown', handleBackdropClick);
        };
    }, [isOpen, value, options, otherErrors]);

    useEffect(() => {
        if (Object.keys(otherErrors).length > 0 && menuRef.current) {
            // Use setTimeout to ensure the error message DOM has rendered
            setTimeout(() => {
                if (menuRef.current) {
                    menuRef.current.scrollTop = menuRef.current.scrollHeight;
                }
            }, 100);
        }
    }, [otherErrors]);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [visibleCount, setVisibleCount] = useState(0);
    const [isMeasuring, setIsMeasuring] = useState(false);
    const chipsContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        let isCancelled = false;

        if (!isOpen && value.length > 0) {
            const calculateVisible = () => {
                if (isCancelled) return;

                setIsMeasuring(true);
                // Temporarily render all to measure their wrapping
                setVisibleCount(value.length);

                // Wait for two frames to ensure DOM is updated and painted
                requestAnimationFrame(() => {
                    if (isCancelled) return;
                    requestAnimationFrame(() => {
                        if (isCancelled) return;

                        const container = chipsContainerRef.current;
                        if (!container) {
                            setIsMeasuring(false);
                            return;
                        }

                        const children = Array.from(
                            container.children
                        ) as HTMLElement[];
                        // Filter out the +N indicator if it somehow exists
                        const chips = children.filter(
                            (c) => !c.classList.contains('more-chips-indicator')
                        );

                        if (chips.length === 0) {
                            setIsMeasuring(false);
                            return;
                        }

                        const firstChild = chips[0];
                        const baseTop = firstChild.offsetTop;
                        let wrappingIndex = -1;

                        // Identify the first chip that wraps to Row 2
                        for (let i = 1; i < chips.length; i++) {
                            if (chips[i].offsetTop > baseTop + 2) {
                                wrappingIndex = i;
                                break;
                            }
                        }

                        if (wrappingIndex !== -1) {
                            // If index is 1, it means the very first chip fits but the second wraps.
                            // We show (wrappingIndex - 1) chips to leave space for +N
                            setVisibleCount(Math.max(1, wrappingIndex - 1));
                        } else {
                            // Everything fits on Row 1
                            setVisibleCount(value.length);
                        }
                        setIsMeasuring(false);
                    });
                });
            };

            calculateVisible();
            window.addEventListener('resize', calculateVisible);
            return () => {
                isCancelled = true;
                window.removeEventListener('resize', calculateVisible);
            };
        } else if (value.length === 0) {
            setVisibleCount(0);
        } else if (isOpen) {
            setVisibleCount(value.length);
        }
    }, [value, isOpen]);

    const getSelectedChips = () => {
        const currentValues = Array.isArray(value) ? value : [];
        if (currentValues.length === 0) {
            return (
                <span className="placeholder-text">
                    {placeholder || 'Select options'}
                </span>
            );
        }

        const visibleValues = currentValues.slice(0, visibleCount);
        const remainingCount = currentValues.length - visibleCount;

        return (
            <>
                {visibleValues.map((val) => {
                    const baseKey = getBaseKey(val);
                    const option = (options || []).find(
                        (opt) => opt.key === baseKey
                    );
                    let displayName = option?.name || baseKey;

                    if (val.includes(': ')) {
                        const subVal = val.split(': ')[1];
                        displayName = `${displayName} : ${subVal}`;
                    }

                    return (
                        <span key={val} className="selected-chip">
                            {displayName}
                            <button
                                type="button"
                                className="chip-remove"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleOnChange(
                                        currentValues.filter((v) => v !== val)
                                    );
                                }}
                                disabled={disabled}
                            >
                                ×
                            </button>
                        </span>
                    );
                })}
                {remainingCount > 0 && (
                    <span
                        className="more-chips-indicator"
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsModalOpen(true);
                        }}
                    >
                        +{remainingCount}
                    </span>
                )}
            </>
        );
    };

    const renderModal = () => {
        if (!isModalOpen) return null;

        return ReactDOM.createPortal(
            <div
                className="custom-modal-overlay"
                onClick={() => setIsModalOpen(false)}
            >
                <div
                    className="custom-modal-content"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="custom-modal-header">
                        <h3 className="modal-title"> Channel of Expansion</h3>
                        <button
                            className="modal-close-btn"
                            onClick={() => setIsModalOpen(false)}
                        >
                            ×
                        </button>
                    </div>
                    <div className="custom-modal-body">
                        <div className="chips-container-modal">
                            {value.map((val) => {
                                const baseKey = getBaseKey(val);
                                const option = (options || []).find(
                                    (opt) => opt.key === baseKey
                                );
                                let displayName = option?.name || baseKey;

                                if (val.includes(': ')) {
                                    const subVal = val.split(': ')[1];
                                    displayName = `${displayName} : ${subVal}`;
                                }

                                return (
                                    <span key={val} className="selected-chip">
                                        {displayName}
                                        <button
                                            type="button"
                                            className="chip-remove"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const updated = value.filter(
                                                    (v) => v !== val
                                                );
                                                handleOnChange(updated);
                                                if (updated.length === 0)
                                                    setIsModalOpen(false);
                                            }}
                                            disabled={disabled}
                                        >
                                            ×
                                        </button>
                                    </span>
                                );
                            })}
                        </div>
                    </div>
                    <div className="custom-modal-footer pt-4 pb-0">
                        {/* <button className="btn btn-primary" onClick={() => setIsModalOpen(false)}>Close</button> */}
                        <Button
                            type="button"
                            className="bottom-cut"
                            style={primaryButtonStyle}
                            onClick={() => setIsModalOpen(false)}
                        >
                            <span className="bottom-cut-span"></span>
                            Close
                        </Button>
                    </div>
                </div>
            </div>,
            document.body
        );
    };

    return (
        <div
            ref={wrapperRef}
            className={`mb-0 custom-multiselect form-group ${
                disabled ? 'Disabled' : ''
            }`}
            style={{
                position: 'relative',
                width: '100%',
                display: 'block'
            }}
        >
            <div
                ref={triggerRef}
                className={`mb-0 custom-expansion-dropdown search-dropdown-container ${name}-adv-dropdown ${
                    disabled ? 'Disabled' : ''
                }`}
                onClick={() => {
                    if (!disabled) setIsOpen(!isOpen);
                }}
                style={{
                    position: 'relative',
                    width: '100%',
                    border: 'none'
                }}
            >
                {isOpen ? (
                    <ClayInput
                        className="custom_input custom-select"
                        id={name}
                        name={name}
                        disabled={disabled}
                        placeholder={placeholder}
                        autoComplete="off"
                        onChange={(e) => setInputValue(e?.target?.value)}
                        type="text"
                        value={inputValue}
                        autoFocus
                        style={{ width: '100%' }}
                    />
                ) : (
                    <div
                        ref={chipsContainerRef}
                        className="custom_input custom-select chips-container"
                        style={{
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            backgroundColor: disabled ? '#e9ecef' : 'white',
                            border: '1px solid #ced4da',
                            borderRadius: '0.25rem',
                            width: '100%',
                            boxSizing: 'border-box',
                            visibility: isMeasuring ? 'hidden' : 'visible',
                            alignItems: 'center',
                            display: 'flex'
                        }}
                    >
                        {getSelectedChips()}
                    </div>
                )}
            </div>
            {errors?.[name] && (
                <div className="invalid-feedback" style={{ display: 'block' }}>
                    {(errors as any)[name]?.message}
                </div>
            )}

            {isOpen &&
                filteredOptions &&
                filteredOptions.length > 0 &&
                ReactDOM.createPortal(
                    <>
                        <div
                            className="dropdown-portal-overlay "
                            style={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                zIndex: 9999,
                                backgroundColor: 'transparent'
                            }}
                        />
                        <div
                            id={`portal-${name}`}
                            ref={menuRef}
                            className="search-dropdown-container-menu custom-multiSelect-inline"
                            style={menuStyle}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <DropDown.ItemList>
                                {filteredOptions.map((item) => {
                                    const checked = isChecked(item.key);
                                    const isOther = isOtherOption(item);

                                    return (
                                        <React.Fragment key={item.key}>
                                            <DropDown.Item
                                                className={
                                                    checked
                                                        ? 'list-item-active'
                                                        : ''
                                                }
                                            >
                                                <ClayCheckbox
                                                    checked={checked}
                                                    onChange={() =>
                                                        handleSelect(item.key)
                                                    }
                                                    onClick={(e) =>
                                                        e.stopPropagation()
                                                    }
                                                    value={item.key}
                                                    name={name}
                                                    label={item.name}
                                                />
                                            </DropDown.Item>
                                            {checked && isOther && (
                                                <div
                                                    style={{
                                                        padding: '0 1rem'
                                                    }}
                                                    onClick={(e) =>
                                                        e.stopPropagation()
                                                    }
                                                    className="dropdown-input mt-2"
                                                >
                                                    <ClayInput
                                                        placeholder="Please specify"
                                                        type="text"
                                                        value={getOtherInputValue(
                                                            item.key
                                                        )}
                                                        onChange={(e) =>
                                                            handleOtherInputChange(
                                                                item.key,
                                                                e.target.value
                                                            )
                                                        }
                                                        autoFocus
                                                    />
                                                    {otherErrors[item.key] && (
                                                        <div
                                                            style={{
                                                                color: '#da1039',
                                                                fontSize:
                                                                    '0.875rem',
                                                                marginTop: '4px'
                                                            }}
                                                        >
                                                            {
                                                                otherErrors[
                                                                    item.key
                                                                ]
                                                            }
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </DropDown.ItemList>
                        </div>
                    </>,
                    document.body
                )}
            {renderModal()}
        </div>
    );
};

export default CustomMultiSelectInline;
